"use client";
import React, { useState } from "react";
import { useRouter } from "next/navigation";

import { NextImage } from "../NextImage";
import { QRPreviewData } from "../QRPreview";
import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import { QR_PAGE_INDEX, updateIndex } from "@/utils/qrEditorNavigatorHelper";
import { generateQrDataClone, setNewQRData } from "@/utils/qrEditorHelper";

import { useToast } from "@/hooks/ui/useToast";

import { UserContext } from "@/contexts/UserContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { PreviewProps } from "./types";

import "./styles.scss";

export const Preview = ({
  items,
  dataQA,
  ctaStyle,
  previewTranslations,
  defaultPreviewTitle,
}: PreviewProps) => {
  const router = useRouter();
  const { toast } = useToast();
  const qrEditorContext = React.useContext(QREditorContext);
  const userContext = React.useContext(UserContext);
  const { setSource } = React.useContext(AmplitudeContext);

  // state to prevent select website as default type in qrEditorContext
  const [initialPreview, setInitialPreview] = React.useState(items[0].id);
  const [activePreview, setActivePreview] = useState(items[0].id);

  const [isQRSelected, setIsQRSelected] = React.useState(false);
  const [formURL, setFormURL] = React.useState("");

  const activePreviewItem = items.filter(
    (item) => item.id === initialPreview || item.id === activePreview
  );

  const qrTypeActive = `${activePreview === "links" ? "listoflinks" : activePreview}`;

  React.useEffect(() => {
    setIsQRSelected(false);
    qrEditorContext?.setEventSource(undefined);
    qrEditorContext?.setGridSelectEvent(false);
    setNewQRData(qrEditorContext, initialPreview, initialPreview);
  }, []);

  React.useEffect(() => {
    if (isQRSelected) {
      if (qrEditorContext?.qrData.currentPageIndex < QR_PAGE_INDEX.PAYMENT) {
        setSource("cta_buttons");
        qrEditorContext.setEventSource("cta_buttons");
        updateIndex({
          newIndex: qrEditorContext?.qrData.currentPageIndex + 1,
          qrEditorContext,
          userContext,
          toast,
          router,
        });

        router.push(formURL);
      }
    }
  }, [isQRSelected]);

  const handlePreviewButton = (url) => {
    const newQRData = generateQrDataClone(qrEditorContext, qrTypeActive);
    if (newQRData) newQRData.currentPageIndex = 0;
    setTimeout(() => {
      qrEditorContext?.setQrData(newQRData);
    }, 0);
    setFormURL(url);
    setIsQRSelected(true);
  };

  const updatePreview = (type) => {
    setActivePreview(type);
    setInitialPreview(undefined);
    const qrType = type === "links" ? "listoflinks" : type;
    if (!isQRSelected) {
      setNewQRData(qrEditorContext, qrType, qrType);
    }
  };

  return (
    <div
      data-testid="Preview"
      className="Preview"
    >
      <div className="Preview__buttons Preview__col qr-buttons">
        <div className="Preview__buttons__layout__grid">
          {items?.map((item) => {
            return (
              <Button
                dataQA={item.data_qa}
                key={`preview-${item.id}`}
                bold
                icon={
                  <div className="button__icon">
                    <div>
                      <NextImage
                        src={`../../svg/icon-qr-${item.id}.svg`}
                        width={25}
                        height={25}
                        alt={`QR ${item.id} icon`}
                        title={`QR ${item.id} icon`}
                        loading="lazy"
                      />
                    </div>
                  </div>
                }
                active={item.id === initialPreview || item.id === activePreview}
                onClick={() => updatePreview(item.id)}
                label={item.title}
                className="child qr-home-button"
              />
            );
          })}
        </div>
      </div>

      <div className="Preview__layout__template">
        <div className="Preview__contents  Preview__col">
          {activePreviewItem?.map(() => (
            <>
              <QRPreviewData
                data={qrEditorContext?.qrData}
                previewTranslations={previewTranslations}
                isQRInit={true}
                defaultPreviewTitle={defaultPreviewTitle}
              />
            </>
          ))}
        </div>
        <div className="Preview__contents  Preview__col">
          {activePreviewItem?.map((item, ind: number) => (
            <div
              className="Preview__content"
              key={ind}
            >
              {item.richtext.map((item, i: number) => (
                <Typography
                  key={i}
                  Tag={item.tag}
                  size={item.size}
                  spacing={item.spacing}
                >
                  {item.list
                    ? item.list.map((e, ind: number) => (
                        <li key={ind}>
                          {<span dangerouslySetInnerHTML={{ __html: e["item"] }}></span>}
                        </li>
                      ))
                    : item.content}
                </Typography>
              ))}

              <Button
                onClick={() => {
                  setSource("cta_buttons");
                  handlePreviewButton(`${item.ctaLink}/${qrTypeActive}`);
                }}
                label={item.ctaLabel}
                dataQA={dataQA}
                rounded={!!ctaStyle}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
